import React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AssignmentIcon from "@mui/icons-material/Assignment";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ChecklistIcon from "@mui/icons-material/Checklist";
import QuizRecordTable from "./QuizRecordTable";
import CheckpointRecordTable from "./CheckpointRecordTable";
import ManagerInputRecordTable from "./ManagerInputRecordTable";
import DocumentUploadRecordTable from "./DocumentUploadRecordTable";
import SignatureRecordTable from "./SignatureRecordTable";
import FormRecordTable from "./FormRecordTable";
import RoleplayRecordTable from "./RoleplayRecordTable";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";
import { getRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_learning_records_for_store,
  API_get_learning_record_detail_for_store,
} from "../../../constants";

const RECORD_TYPE_OPTIONS = [
  "Quiz",
  "Checkpoint",
  "Manager Input",
  "Document Upload",
  "Signature",
  "Form",
  "Practice Scenario",
];

const RECORD_SORT_OPTIONS = [
  { value: "completion_desc", label: "Completion (high to low)" },
  { value: "completion_asc", label: "Completion (low to high)" },
  { value: "name", label: "Record name (A–Z)" },
  { value: "assigned_desc", label: "Most assigned" },
];

const recordCompletionPct = (record) =>
  record.assigned_count > 0
    ? (record.total_submissions / record.assigned_count) * 100
    : 0;

function RecordsDisplay(props) {
  const [selectedRecord, setSelectedRecord] = React.useState(null);

  // Report the drilled-into record up to the parent so the header breadcrumb can
  // show "By Record › Quiz 1". Clears on deselect and on unmount (tab switch).
  React.useEffect(() => {
    if (!props.onDetailChange) return undefined;
    if (selectedRecord) {
      props.onDetailChange({
        label: selectedRecord.name,
        onClear: () => setSelectedRecord(null),
      });
    } else {
      props.onDetailChange(null);
    }
    return () => {
      if (props.onDetailChange) props.onDetailChange(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecord]);

  const [records, setRecords] = React.useState([]);
  const [searchInput, setSearchInput] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState([]);
  const [filterMenuAnchor, setFilterMenuAnchor] = React.useState(null);
  const [sortBy, setSortBy] = React.useState("completion_desc");
  const [sortMenuAnchor, setSortMenuAnchor] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = React.useState(false);
  const [error, setError] = React.useState(null);

  const toggleType = (type) => {
    setTypeFilter((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // Fetch records from API
  React.useEffect(() => {
    const fetchRecords = async () => {
      if (!props.selectedStore?.id || !props.homeCompany) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const api_params = {
          company_id: props.homeCompany,
          store_id: props.selectedStore.id,
        };

        const res = await getRightworkAPI(
          API_get_learning_records_for_store,
          api_params
        );

        if (res.status === 200) {
          const apiRecords = res.data.redux?.payload?.records || [];

          // Transform API response to match component expectations
          const transformedRecords = apiRecords.map((record) => ({
            id: record.uuid,
            uuid: record.uuid,
            // Map backend record_type to frontend display type
            type: mapRecordType(record.record_type),
            record_type: record.record_type,
            name: record.name,
            section_name: record.section_name,
            section_uuid: record.section_uuid,
            // Summary counts
            total_submissions: record.total_completed_count,
            assigned_count: record.total_assigned_count,
            completion_percentage: record.completion_percentage,
            // Type-specific metadata
            ...extractMetadata(record.record_type, record.metadata),
            // Empty arrays for employee-level data (would need additional API)
            submissions: [],
            incomplete_assignments: [],
          }));

          setRecords(transformedRecords);
        }
      } catch (err) {
        console.error("Failed to fetch learning records:", err);
        setError("Failed to load records. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecords();
  }, [props.selectedStore?.id, props.homeCompany]);

  // Map backend record_type to frontend display type
  function mapRecordType(recordType) {
    const typeMap = {
      quiz: "Quiz",
      checkpoint: "Checkpoint",
      manager_input: "Manager Input",
      document_upload: "Document Upload",
      signature: "Signature",
      form: "Form",
      roleplay: "Practice Scenario",
    };
    return typeMap[recordType] || recordType;
  }

  // Extract type-specific fields from metadata
  function extractMetadata(recordType, metadata) {
    if (!metadata) return {};

    switch (recordType) {
      case "quiz":
        return {
          question_count: metadata.question_count,
          pass_percentage: metadata.pass_percentage,
          retake_limit: metadata.retake_limit,
        };
      case "checkpoint":
        return {
          category: metadata.category,
        };
      case "manager_input":
        return {
          description: metadata.description,
          required: metadata.required,
        };
      case "document_upload":
        return {
          description: metadata.description,
          required: metadata.required,
          requires_approval: metadata.requires_approval,
        };
      case "signature":
        return {
          signature_mode: metadata.signature_mode || "pdf",
          pdf_name: metadata.pdf_name,
          statement_preview: metadata.statement_preview || "",
          total_pages: metadata.total_pages || 1,
          signature_fields: metadata.signature_fields || 1,
          text_fields: metadata.text_fields || 0,
        };
      case "form":
        return {
          description: metadata.description,
          fields: metadata.fields || [],
          completed_by_role: metadata.completed_by_role,
          scoring_enabled: metadata.scoring_enabled,
          pass_threshold: metadata.pass_threshold,
          field_count: metadata.field_count,
        };
      case "roleplay":
        return {
          description: metadata.description,
          criteria_count: metadata.criteria_count,
          max_turns: metadata.max_turns,
        };
      default:
        return metadata;
    }
  }

  // Fetch detail data when a record is clicked
  async function handleRecordClick(record) {
    if (!props.selectedStore?.id || !props.homeCompany) return;

    setIsLoadingDetail(true);

    try {
      const api_params = {
        company_id: props.homeCompany,
        store_id: props.selectedStore.id,
        record_summary_uuid: record.uuid,
      };

      const res = await getRightworkAPI(
        API_get_learning_record_detail_for_store,
        api_params
      );

      if (res.status === 200) {
        const detail = res.data.redux?.payload;

        if (detail) {
          // Merge summary data with detail data
          const detailedRecord = {
            ...record,
            // Override with detail API data
            submissions: detail.submissions || [],
            incomplete_assignments: detail.incomplete_assignments || [],
            // Needed to grade form submissions (the summary uuid is the
            // record's, not the underlying form's or section's).
            form_uuid: detail.form_uuid,
            section_uuid: detail.section_uuid,
            // Lets a manager record/edit a Manager Input directly from Records.
            manager_input_block_uuid: detail.manager_input_block_uuid,
            // Lets the roleplay review modal load an employee's transcript.
            roleplay_uuid: detail.roleplay_uuid,
          };
          setSelectedRecord(detailedRecord);
        }
      }
    } catch (err) {
      console.error("Failed to fetch record detail:", err);
      // Still show the record with empty submissions on error
      setSelectedRecord(record);
    } finally {
      setIsLoadingDetail(false);
    }
  }

  // Get icon component and colors for record type
  function getRecordTypeDisplay(type) {
    switch (type) {
      case "Quiz":
        return {
          icon: QuizOutlinedIcon,
          bgColor: "bg-blue-100",
          iconColor: "text-blue-600",
        };
      case "Checkpoint":
        return {
          icon: ChecklistIcon,
          bgColor: "bg-red-100",
          iconColor: "text-red-600",
        };
      case "Manager Input":
        return {
          icon: RateReviewOutlinedIcon,
          bgColor: "bg-amber-100",
          iconColor: "text-amber-600",
        };
      case "Document Upload":
        return {
          icon: UploadFileIcon,
          bgColor: "bg-green-100",
          iconColor: "text-green-600",
        };
      case "Signature":
        return {
          icon: DrawOutlinedIcon,
          bgColor: "bg-purple-100",
          iconColor: "text-purple-600",
        };
      case "Form":
        return {
          icon: DescriptionOutlinedIcon,
          bgColor: "bg-indigo-100",
          iconColor: "text-indigo-600",
        };
      case "Practice Scenario":
        return {
          icon: ConnectWithoutContactIcon,
          bgColor: "bg-violet-100",
          iconColor: "text-violet-600",
        };
      default:
        return {
          icon: AssignmentIcon,
          bgColor: "bg-slate-100",
          iconColor: "text-slate-600",
        };
    }
  }

  const filteredRecords = React.useMemo(() => {
    const normalizedSearch = searchInput.trim().toLowerCase();
    const filtered = records.filter((record) => {
      if (typeFilter.length && !typeFilter.includes(record.type)) return false;
      if (!normalizedSearch) return true;
      const type = (record.type || "").toLowerCase();
      const name = (record.name || "").toLowerCase();
      const section = (record.section_name || "").toLowerCase();
      return (
        type.includes(normalizedSearch) ||
        name.includes(normalizedSearch) ||
        section.includes(normalizedSearch)
      );
    });
    return [...filtered].sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      switch (sortBy) {
        case "completion_asc":
          return (
            recordCompletionPct(a) - recordCompletionPct(b) ||
            nameA.localeCompare(nameB)
          );
        case "name":
          return nameA.localeCompare(nameB);
        case "assigned_desc":
          return (
            (b.assigned_count || 0) - (a.assigned_count || 0) ||
            nameA.localeCompare(nameB)
          );
        case "completion_desc":
        default:
          return (
            recordCompletionPct(b) - recordCompletionPct(a) ||
            nameA.localeCompare(nameB)
          );
      }
    });
  }, [records, searchInput, typeFilter, sortBy]);

  const exportRecordsSummaryCSV = () => {
    const rows = filteredRecords.map((record) => {
      const completionPct =
        record.assigned_count > 0
          ? Math.round(
              (record.total_submissions / record.assigned_count) * 100
            )
          : 0;
      return {
        "Record Type": record.type,
        "Record Name": record.name,
        "Section Name": record.section_name,
        Completed: record.total_submissions,
        Assigned: record.assigned_count,
        "Completion %": `${completionPct}%`,
      };
    });

    if (rows.length === 0) return;
    const csv = objectToCSV(rows);
    const storeName = props.selectedStore?.store_name || "store";
    downloadCSV(
      csv,
      `training_records_${storeName.replace(/\s+/g, "_")}.csv`
    );
  };

  // Loading state
  if (isLoading || isLoadingDetail) {
    return (
      <div className="px-6 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadSpinner />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="px-6 py-8">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (records.length === 0) {
    return (
      <div className="px-6 py-8">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <AssignmentIcon
            className="text-slate-300 mx-auto mb-4"
            style={{ fontSize: "48px" }}
          />
          <p className="text-slate-600">No training records found for this store.</p>
          <p className="text-slate-400 text-sm mt-2">
            Records will appear here when employees are enrolled in training tracks
            with quizzes, checkpoints, or other completion requirements.
          </p>
        </div>
      </div>
    );
  }

  // If a record is selected, show the detail table
  if (selectedRecord) {
    if (selectedRecord.type === "Quiz") {
      return (
        <QuizRecordTable
          record={selectedRecord}
          onBack={() => setSelectedRecord(null)}
          homeCompany={props.homeCompany}
          language={props.language || "english"}
          employeeAvatarColorByUserId={props.employeeAvatarColorByUserId}
        />
      );
    } else if (selectedRecord.type === "Checkpoint") {
      return (
        <CheckpointRecordTable
          record={selectedRecord}
          onBack={() => setSelectedRecord(null)}
          employeeAvatarColorByUserId={props.employeeAvatarColorByUserId}
        />
      );
    } else if (selectedRecord.type === "Manager Input") {
      return (
        <ManagerInputRecordTable
          record={selectedRecord}
          onBack={() => setSelectedRecord(null)}
          employeeAvatarColorByUserId={props.employeeAvatarColorByUserId}
          homeCompany={props.homeCompany}
        />
      );
    } else if (selectedRecord.type === "Document Upload") {
      return (
        <DocumentUploadRecordTable
          record={selectedRecord}
          onBack={() => setSelectedRecord(null)}
          employeeAvatarColorByUserId={props.employeeAvatarColorByUserId}
        />
      );
    } else if (selectedRecord.type === "Signature") {
      return (
        <SignatureRecordTable
          record={selectedRecord}
          onBack={() => setSelectedRecord(null)}
          employeeAvatarColorByUserId={props.employeeAvatarColorByUserId}
        />
      );
    } else if (selectedRecord.type === "Form") {
      return (
        <FormRecordTable
          record={selectedRecord}
          onBack={() => setSelectedRecord(null)}
          employeeAvatarColorByUserId={props.employeeAvatarColorByUserId}
          homeCompany={props.homeCompany}
        />
      );
    } else if (selectedRecord.type === "Practice Scenario") {
      return (
        <RoleplayRecordTable
          record={selectedRecord}
          onBack={() => setSelectedRecord(null)}
          employeeAvatarColorByUserId={props.employeeAvatarColorByUserId}
          homeCompany={props.homeCompany}
        />
      );
    }
  }

  // Otherwise, show the summary table
  return (
    <div className="px-6 py-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={(e) => setSortMenuAnchor(e.currentTarget)}
            className={`flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium transition-colors hover:border-slate-300 hover:bg-slate-50 ${
              sortBy !== "completion_desc" ? "text-violet-700" : "text-slate-800"
            }`}
          >
            <SwapVertIcon style={{ fontSize: "18px" }} />
            Sort
            <KeyboardArrowDownIcon
              className="text-slate-500"
              style={{ fontSize: "18px" }}
            />
          </button>
          <button
            type="button"
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            className={`flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium transition-colors hover:border-slate-300 hover:bg-slate-50 ${
              typeFilter.length > 0 ? "text-violet-700" : "text-slate-800"
            }`}
          >
            <FilterAltOutlinedIcon style={{ fontSize: "18px" }} />
            Filter
            {typeFilter.length > 0 && (
              <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-violet-100 px-1.5 text-[11px] font-semibold text-violet-700">
                {typeFilter.length}
              </span>
            )}
            <KeyboardArrowDownIcon
              className="text-slate-500"
              style={{ fontSize: "18px" }}
            />
          </button>
          <div className="w-[280px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 transform text-slate-400"
                style={{ fontSize: "20px" }}
              />
              <input
                className="h-[46px] w-full rounded-lg border border-slate-200 pl-10 pr-4 text-sm outline-none transition-all duration-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                type="text"
                placeholder="Search records..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </div>
          </div>
        </div>
        <button
          className="flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg bg-emerald-600 px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
          onClick={exportRecordsSummaryCSV}
        >
          <DownloadIcon style={{ fontSize: "18px" }} />
          Export CSV
        </button>
      </div>

      {/* Filter menu: record type */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 240,
            borderRadius: "12px",
            boxShadow: "0 10px 30px -12px rgba(0,0,0,0.25)",
            border: "1px solid #e2e8f0",
          },
        }}
      >
        <div className="px-4 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
          Record type
        </div>
        {RECORD_TYPE_OPTIONS.map((type) => {
          const checked = typeFilter.includes(type);
          return (
            <MenuItem
              key={type}
              onClick={() => toggleType(type)}
              sx={{ py: 0.25 }}
            >
              <Checkbox
                checked={checked}
                size="small"
                disableRipple
                sx={{
                  color: "#cbd5e1",
                  "&.Mui-checked": { color: "#6d28d9" },
                  p: 0.5,
                  mr: 1,
                }}
              />
              <ListItemText
                primaryTypographyProps={{ fontSize: 14, color: "#334155" }}
              >
                {type}
              </ListItemText>
            </MenuItem>
          );
        })}
        {typeFilter.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={() => setTypeFilter([])}
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#6d28d9",
                justifyContent: "center",
                py: 1,
              }}
            >
              Clear filter
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Sort menu */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 240,
            borderRadius: "12px",
            boxShadow: "0 10px 30px -12px rgba(0,0,0,0.25)",
            border: "1px solid #e2e8f0",
          },
        }}
      >
        <div className="px-4 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
          Sort by
        </div>
        {RECORD_SORT_OPTIONS.map((option) => {
          const active = sortBy === option.value;
          return (
            <MenuItem
              key={option.value}
              onClick={() => {
                setSortBy(option.value);
                setSortMenuAnchor(null);
              }}
              sx={{
                fontSize: 14,
                color: active ? "#6d28d9" : "#334155",
                py: 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 30 }}>
                {active && <CheckIcon sx={{ fontSize: 18, color: "#6d28d9" }} />}
              </ListItemIcon>
              {option.label}
            </MenuItem>
          );
        })}
      </Menu>
      <div className="-mx-6 border-y border-slate-200 bg-white">
        {filteredRecords.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Record Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Record Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    In Section
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Completion Progress
                  </th>
                  <th className="relative px-6 py-4">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredRecords.map((record) => {
                  const typeDisplay = getRecordTypeDisplay(record.type);
                  const IconComponent = typeDisplay.icon;
                  const completionPct =
                    record.assigned_count > 0
                      ? Math.round(
                          (record.total_submissions / record.assigned_count) * 100
                        )
                      : 0;

                  return (
                    <tr
                      key={record.id}
                      onClick={() => handleRecordClick(record)}
                      className="hover:bg-slate-50 cursor-pointer transition-colors duration-150"
                    >
                      <td className="align-middle px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-8 h-8 ${typeDisplay.bgColor} rounded-lg flex items-center justify-center shrink-0`}
                          >
                            <IconComponent
                              className={typeDisplay.iconColor}
                              style={{ fontSize: "16px" }}
                            />
                          </div>
                          <span className="text-xs font-medium text-slate-900">
                            {record.type}
                          </span>
                        </div>
                      </td>
                      <td className="align-middle px-6 py-3">
                        <div className="text-xs font-semibold text-slate-900">
                          {record.name}
                        </div>
                        {(record.type === "Quiz" || record.type === "Checkpoint") &&
                          record.question_count && (
                            <div className="text-xs text-slate-500 mt-1">
                              {record.question_count} questions
                              {record.pass_percentage
                                ? ` • ${record.pass_percentage}% pass rate`
                                : ""}
                            </div>
                          )}
                      </td>
                      <td className="align-middle px-6 py-3 whitespace-nowrap">
                        <div className="text-xs text-slate-600">
                          {record.section_name}
                        </div>
                      </td>
                      <td className="align-middle px-6 py-3">
                        <div className="space-y-2 min-w-[250px]">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <AssignmentIcon
                                className="text-slate-400"
                                style={{ fontSize: "16px" }}
                              />
                              <span className="text-xs text-slate-600">
                                {record.total_submissions} / {record.assigned_count}{" "}
                                completed
                              </span>
                            </div>
                            <span className="text-xs font-medium text-slate-900">
                              {completionPct}%
                            </span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-violet-500 to-violet-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${completionPct}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="align-middle px-6 py-3 whitespace-nowrap text-right text-xs font-medium">
                        <div className="flex items-center justify-end">
                          <ChevronRightIcon className="text-slate-400 transition-colors" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <AssignmentIcon
              className="text-slate-300 mx-auto mb-4"
              style={{ fontSize: "48px" }}
            />
            <p className="text-slate-600 font-medium mb-2">No records found</p>
            <p className="text-slate-400 text-sm">
              Try a different search term to find a record.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default RecordsDisplay;
